import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getDetailArticle,
  getEvent,
  getEventDetail,
  getEventJourney,
  getEventSeries,
  getEventSeriesDetail,
  getGalleryArticle,
  getGroupMatch,
  getHighlightArticle,
  getKnockoutMatch,
  getScheduleMatch,
  getVenue,
  getWhatsNew,
  getSection,
  getEventRegionSeries,
  getEventHallOfFame,
  getTimelineSeries,
  postRegistrationForm,
  postLogin,
  getCurrentRegistrationData,
  postUpdateData,
  postAddPlayer,
  getPaymentDetail,
  postProofPayment,
  postUpdatePaymentExpired,
  postTokenLiveScore,
  getCompetititonList,
  getListOfClub,
  getRegionGallery,
  getYearsGallery,
  getMatches,
  getMatchesDetail,
  getMainRegionGallery,
  getCompetitionsDetail,
  getSocAttribute,
  getTimelineSoc,
  getMainRegionSoc,
  getSocChampions,
  getMatchTopStats,
  getLiveMatchesDetail,
  getCurrentEventSeries,
  getCityCategories,
  postRegistrationDanceForm,
  postAddDancer,
  postUpdateDanceData,
  getEventCategory,
  getMatchList,
  getMatchDetail,
  getCompetitionsDetailV2,
  getKnockoutBracket,
  getQualifierBracket,
  getVideos,
  getActivity,
  getActivityDetail,
  getDrawingSoc,
  getGoldenTicketSchools,
} from "./services";
import { useMemo, useRef, useState } from "react";

export const useFetchHighlightArticle = (slug = "blitar", enabled = true) => {
  return useQuery(
    ["Fetch Highlight Article", slug],
    () => getHighlightArticle(slug),
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useFetchGalleryArticle = (slug = "blitar", enabled = true) => {
  return useQuery(
    ["Fetch Gallery Article", slug],
    () => getGalleryArticle(slug),
    {
      enabled,
    },
  );
};

export const useFetchDetailArticle = (
  slug = "",
  region = "blitar",
  enabled = true,
) => {
  return useQuery(
    ["Fetch Detail Article"],
    () => getDetailArticle(slug, region),
    {
      enabled: !!slug && enabled,
    },
  );
};

export const useFetchScheduleMatch = (slug = "blitar", enabled = true) => {
  const category =
    slug !== "semarang" ? "Futsal SMA,Futsal SMP" : "Basket,Futsal,Dance";

  return useQuery(
    ["Fetch Schedule Match", slug],
    () => getScheduleMatch(slug, category),
    {
      enabled,
    },
  );
};

export const useFetchGroupMatch = (slug = "blitar", enabled = true) => {
  const category =
    slug !== "semarang" ? "Futsal SMA,Futsal SMP" : "Basket,Futsal,Dance";

  return useQuery(
    ["Fetch Group Match", slug],
    () => getGroupMatch(slug, category),
    {
      enabled,
    },
  );
};

export const useFetchKnockoutMatch = (slug = "blitar", enabled = true) => {
  const category =
    slug !== "semarang" ? "Futsal SMA,Futsal SMP" : "Basket,Futsal,Dance";

  return useQuery(
    ["Fetch Knockout Match", slug],
    () => getKnockoutMatch(slug, category),
    {
      enabled,
    },
  );
};

export const useFetchWhatNew = (slug = "blitar", enabled = true) => {
  return useQuery(["Fetch Whats New", slug], () => getWhatsNew(slug), {
    enabled,
  });
};

export const useFetchVenue = (slug = "blitar", enabled = true) => {
  return useQuery(["Fetch Venue", slug], () => getVenue(slug), { enabled });
};

export const useFetchEvent = (enabled = true) => {
  return useQuery(["Fetch Event"], () => getEvent(), { enabled });
};

export const useFetchEventJourney = (enabled = true) => {
  return useQuery(["Fetch Event Journey"], () => getEventJourney(), {
    enabled,
  });
};

export const useFetchEventDetail = (slug = "", enabled = true) => {
  return useQuery(["Fetch Event Detail", slug], () => getEventDetail(slug), {
    enabled,
  });
};

export const useFetchEventSeries = (enabled = true) => {
  return useQuery(["Fetch Event Series"], getEventSeries, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchRegionEventSeries = (enabled = true) => {
  return useQuery(["Fetch Region Event Series"], getEventRegionSeries, {
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useFetchEventSeriesDetail = (slug = "", enabled = true) => {
  return useQuery(
    ["Fetch Event Series Detail", slug],
    () => getEventSeriesDetail(slug),
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useFetchSection = (enabled = true) => {
  return useQuery(["Fetch Section"], () => getSection(), { enabled });
};

export const useFetchGetCurrentEvent = () => {
  const queryClient = useQueryClient();
  const dataRef = useRef([]);

  const [isLoading, setIsLoading] = useState(false);

  const fetchQuery = async (val, i) => {
    const queryKey = [`Fetch Event Series Detail ${i}`];
    const queryFn = () => getEventSeriesDetail(val);
    const currentData = await queryClient.fetchQuery({ queryKey, queryFn });

    return currentData;
  };

  const getCurrentEvent = async (slug = [], callback) => {
    return new Promise((resolve) => {
      Array.from(slug)?.map(async (val, i) => {
        fetchQuery(val, i).then((data) => {
          if (typeof dataRef.current !== "object") return;
          setIsLoading(true);
          dataRef.current = [
            ...dataRef.current,
            ...(data?.data?.data?.eventSeries ?? []),
          ];

          if (dataRef.current.length >= slug.length) {
            const data = [...dataRef.current];
            setIsLoading(false);
            dataRef.current = [];

            resolve(data);
          }
        });
      });
    });
  };

  return {
    getCurrentEvent,
    isLoading,
  };
};

export const useFetchEventHallOfFame = (slug = "", enabled = true) => {
  return useQuery(
    ["Fetch Event Hall Of Fame", slug],
    () => getEventHallOfFame(slug),
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useFetchTimelineSeries = (slug = "", enabled = true) => {
  return useQuery(
    ["Fetch Timeline Series", slug],
    () => getTimelineSeries(slug),
    {
      enabled,
      refetchOnWindowFocus: false,
      // staleTime: 10000,
    },
  );
};

export const useBulkFetchTimelineEvent = (regionSeries = []) => {
  const queryData =
    regionSeries?.map((data) => ({
      queryKey: `Fetch Event Timeline Series ${data}`,
      queryFn: () => getTimelineSeries(data),
      refetchOnMount: false,
    })) ?? [];

  const dataAllTimelineEventRaw = useQueries({
    queries: queryData,
  });

  const dataAllTimeline = useMemo(() => {
    return [...dataAllTimelineEventRaw]?.map(
      (item) => item?.data?.data?.data?.timeline ?? [],
    );
  }, [dataAllTimelineEventRaw]);

  const isLoading =
    dataAllTimelineEventRaw?.[0]?.isFetching ||
    dataAllTimelineEventRaw?.[0]?.isLoading;

  return {
    isLoading,
    data: dataAllTimeline,
  };
};

const useLazyQuery = (key, fn, options) => {
  const [enabled, setEnabled] = useState(false);
  return [() => setEnabled(true), useQuery(key, fn, { ...options, enabled })];
};

export const useFetchEventRegionSeries = (enabled) => {
  return useQuery(["Fetch Region Event Series"], getEventRegionSeries, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const usePostRegistrationData = () => {
  return useMutation(["Post Registration Data"], postRegistrationForm);
};

export const usePostRegistrationDanceData = () => {
  return useMutation(
    ["Post Registration Dance Data"],
    postRegistrationDanceForm,
  );
};

export const usePostUpdateRegistrationData = () => {
  return useMutation(["Post Update Registration Data"], postUpdateData);
};

export const usePostUpdateDanceRegistrationData = () => {
  return useMutation(
    ["Post Update Dance Registration Data"],
    postUpdateDanceData,
  );
};

export const usePostLogin = () => {
  return useMutation(["Login"], postLogin);
};

export const useFetchCurrentRegistrationData = (enabled = true) => {
  return useQuery(
    ["Fetch current registration data"],
    getCurrentRegistrationData,
    {
      enabled,
      retryOnMount: false,
    },
  );
};

export const usePostAddPlayer = () => {
  return useMutation(["Post Add Player"], postAddPlayer);
};

export const usePostAddDancer = () => {
  return useMutation(["Post Add Dancer"], postAddDancer);
};

export const useGetPaymentDetails = (region_id, enabled = true) => {
  // region_id ikut jadi bagian query key: tanpa itu semua region berbagi satu
  // cache, jadi biaya region yang dibuka duluan kebawa ke region berikutnya.
  return useQuery(
    ["Get Payment Details", region_id],
    () => getPaymentDetail(region_id),
    {
      enabled,
    },
  );
};

export const usePostProofPayment = () => {
  return useMutation(["Post Upload Proof of Payment"], postProofPayment);
};

export const usePostUpdatePaymentExpired = () => {
  return useMutation(["Post Update Payment Expired"], postUpdatePaymentExpired);
};

//LapangBola LiveScore
export const usePostTokenLiveScore = () => {
  return useMutation(["Post Token Live Score"], postTokenLiveScore);
};

export const useBulkFetchEventSeries = (regionSeries = []) => {
  const queryData =
    regionSeries?.map((data) => ({
      queryKey: `Fetch EventSeries Bulk - ${data}`,
      queryFn: () => getEventSeriesDetail(data),
      refetchOnMount: false,
    })) ?? [];

  const dataAllEventSeriesRaw = useQueries({
    queries: queryData,
  });

  const dataAllTimeline = useMemo(() => {
    return [...dataAllEventSeriesRaw]?.map(
      (item) => item?.data?.data?.data?.eventSeries?.[0] ?? [],
    );
  }, [dataAllEventSeriesRaw]);

  const isLoading =
    dataAllEventSeriesRaw?.[0]?.isFetching ||
    dataAllEventSeriesRaw?.[0]?.isRefetching ||
    dataAllEventSeriesRaw?.[0]?.isLoading;

  return {
    isLoading,
    data: dataAllTimeline,
  };
};

export const useFetchRegionGallery = (data, enabled = true) => {
  return useQuery(
    ["List of Gallery Region", data],
    () => getRegionGallery(data),
    {
      enabled,
    },
  );
};

export const useFetchYearsGallery = (data, enabled = true) => {
  return useQuery(
    ["List of Gallery Years", data],
    () => getYearsGallery(data),
    {
      enabled,
    },
  );
};

export const useFetchListOfClub = (enabled = true) => {
  return useQuery(["Fetch List of club"], getListOfClub, {
    enabled,
  });
};

export const useFetchCompetitionList = (enabled = true) => {
  return useQuery(["Fetch Competition List"], getCompetititonList, {
    enabled,
  });
};

export const useFetchMatches = (region_id, enabled = true) => {
  return useQuery(
    ["Fetch Match List", region_id],
    () => getMatches(region_id),
    {
      enabled,
      refetchInterval: 30000, // every 30 seconds
      refetchIntervalInBackground: false,
      staleTime: 60000, // data remains fresh for 1 minute
    },
  );
};

export const useFetchMatchesDetail = (id, enabled = true) => {
  return useQuery(["Fetch Match Detail", id], () => getMatchesDetail(id), {
    enabled,
    refetchInterval: 30000, // every 30 seconds
    refetchIntervalInBackground: false,
    staleTime: 60000, // data remains fresh for 1 minute
  });
};

export const useFetchLiveMatchesDetail = (id, enabled = true) => {
  return useQuery(
    ["Fetch Live Match Detail", id],
    () => getLiveMatchesDetail(id),
    {
      enabled,
      refetchInterval: 30000, // every 30 seconds
      refetchIntervalInBackground: false,
      staleTime: 60000, // data remains fresh for 1 minute
    },
  );
};

export const useFetchClassmentsDetails = (competition_id, enabled = true) => {
  return useQuery(
    ["Fetch Classments Details", competition_id],
    () => getCompetitionsDetail(competition_id),
    {
      enabled,
    },
  );
};

export const useFetchClassmentsDetailsV2 = (region_id, category, enabled = true) => {
  return useQuery(
    ["Fetch Classments Details V2", region_id, category],
    () => getCompetitionsDetailV2(region_id, category),
    {
      enabled: enabled && !!region_id && !!category,
    },
  );
};

export const useFetchKnockoutBracket = (region_id, enabled = true) => {
  return useQuery(
    ["Fetch Knockout Bracket", region_id],
    () => getKnockoutBracket(region_id),
    {
      enabled,
    },
  );
};

export const useFetchQualifierBracket = (region_id, enabled = true) => {
  return useQuery(
    ["Fetch Qualifier Bracket", region_id],
    () => getQualifierBracket(region_id),
    {
      enabled: enabled && !!region_id,
    },
  );
};

export const useFetchMainRegionGallery = (year, enabled = true) => {
  return useQuery(
    ["Fetch main region gallery", year],
    () => getMainRegionGallery(year),
    {
      enabled,
    },
  );
};

export const useBulkFetchGallerySeries = (regionSeries = []) => {
  const queryData =
    regionSeries?.map((data) => ({
      queryKey: `Fetch Gallery Series ${data?.main_region}`,
      queryFn: () => getRegionGallery(data),
      refetchOnMount: false,
    })) ?? [];

  const dataAllGalleryRaw = useQueries({
    queries: queryData,
  });

  const dataAllGallery = useMemo(() => {
    return [...dataAllGalleryRaw]?.map((item) => item?.data?.data?.data ?? []);
  }, [dataAllGalleryRaw]);

  const isLoading =
    dataAllGalleryRaw?.[0]?.isFetching || dataAllGalleryRaw?.[0]?.isLoading;

  return {
    isLoading,
    data: dataAllGallery,
  };
};

export const useFetchSocAttribute = (enabled = true) => {
  return useQuery(["Fetch SOC Attribute"], () => getSocAttribute(), {
    enabled,
  });
};

export const useFetchTimelineSoc = (slug, enabled = true) => {
  return useQuery(["Fetch SOC Timeline", slug], () => getTimelineSoc(slug), {
    enabled,
  });
};

export const useFetchMainRegionSOc = (enabled = true) => {
  return useQuery(["Fetch Main Region SOC"], getMainRegionSoc, {
    enabled,
  });
};

export const useFetchSocChampions = (params, enabled) => {
  return useQuery(
    ["Fetch SOC Region Champions", params],
    () => getSocChampions(params),
    {
      enabled,
    },
  );
};

export const useFetchMatchTopStats = (params, enabled = true) => {
  return useQuery(
    ["Fetch SOC Match Top Stats", params],
    () => getMatchTopStats(params),
    {
      enabled,
    },
  );
};

export const useFetchCurrentEventSeries = (params, enabled = true) => {
  return useQuery(
    // `params` (kategori) wajib ikut di key: tanpa itu pemanggil yang minta
    // kategori tertentu dan yang minta semua kota saling nimpa di cache.
    ["Fetch Current Event Series", params ?? null],
    () => getCurrentEventSeries(params),
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useFetchCityCategories = (enabled = true) => {
  return useQuery(["Fetch City Categories"], getCityCategories, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchEventCategory = (enabled = true) => {
  return useQuery(["Fetch Event Category"], getEventCategory, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchMatchList = (regionId, enabled = true) => {
  return useQuery(
    ["Fetch Match List", regionId],
    () => getMatchList(regionId),
    {
      enabled,
    },
  );
};

export const useFetchMatchDetail = (matchScheduleId, enabled = true) => {
  return useQuery(
    ["Fetch Match Detail", matchScheduleId],
    () => getMatchDetail(matchScheduleId),
    {
      enabled,
    },
  );
};

export const useFetchVideos = (region, enabled = true) => {
  return useQuery(["Fetch Videos", region], () => getVideos(region), {
    enabled,
  });
};

export const useFetchActivity = (region, enabled = true) => {
  return useQuery(["Fetch Activity", region], () => getActivity(region), {
    enabled,
  });
};

export const useFetchActivityDetail = (payload, enabled = true) => {
  return useQuery(
    ["Fetch Activity Detail", payload],
    () => getActivityDetail(payload),
    {
      enabled,
    },
  );
};

export const useFetchDrawingSoc = (enabled = true) => {
  return useQuery(["Fetch Drawing SOC"], () => getDrawingSoc(), {
    enabled,
    refetchOnWindowFocus: false,
    refetchInterval: 3000, // every 3 seconds
    refetchIntervalInBackground: false,
    staleTime: 1000, // data remains fresh for 1 second
  });
};

// category null = kategori itu ga punya golden ticket (Kampus), jadi jangan
// manggil endpoint-nya sama sekali.
export const useFetchGoldenTicketSchools = (category, enabled = true) => {
  return useQuery(
    ["Fetch Golden Ticket Schools", category],
    () => getGoldenTicketSchools(category),
    {
      enabled: enabled && !!category,
    },
  );
};
